import React, { useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import { ErrorBox } from "./support/ErrorBox";
import FormControl from "@mui/material/FormControl";
import { FormikProps } from "formik";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { RequestedPortalData } from "./requested-portal-model";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getRequestAccessCountriesEndpoint } from "./request-access-api";
import { useCargillDefaultRequestReasons } from "./useCargillDefaultRequestReasons";
import { useCargillRequestedPortals } from "./useCargillRequestedPortals";
import { useGetTranslatedCountries } from "./useGetTranslatedCountries";
import { useGetTranslatedReasons } from "./useGetTranslatedReasons";
import { useTranslation } from "next-i18next";

interface RequestedPortalCountry {
  active: string;
  country: string;
}

interface Props {
  formik: FormikProps<RequestedPortalData>;
}

/**
 * Employee Access More Information step / screen.
 * @param param0 E
 * @returns
 */
export function RequestedPortalStep({ formik }: Props) {
  const { t } = useTranslation();
  const other = t("request_access:more_information.other");
  const [showToolTip, setShowToolTip] = useState(false);
  const [selectedAppRefName, setSelectedAppRefName] = useState("");

  const [countryOfBusinessData, setCountryOfBusinessData] = useState<string[]>(
    []
  );
  const [reasonForRequestData, setReasonForRequestData] = useState<string[]>(
    []
  );

  const requestedPortalData = useCargillRequestedPortals();
  const defaultRequestReasons = useCargillDefaultRequestReasons();

  const getTranslatedCountries = useGetTranslatedCountries();
  const getTranslatedReasons = useGetTranslatedReasons();

  const hrRequestKeywords = getKeyWordsHRValidation();

  /**
   * Get the selected Portal's corresponding lists of Countries and Request Access Reasons
   */
  function getRequestedPortalData(e: any) {
    const countriesUrl = getRequestAccessCountriesEndpoint();

    const cargillBusiness = e?.target?.value as string | undefined;

    // reset existing country options, request reason options
    setCountryOfBusinessData([]);
    setReasonForRequestData([]);

    // reset any previous selections for country and request reason
    formik.setFieldValue("country", "", true);
    formik.setFieldValue("requestReason", "", true);

    if (cargillBusiness && cargillBusiness.length > 0) {
      const selectedPortal = requestedPortalData.find(
        ({ name }) => name === cargillBusiness
      );

      const appRefName = selectedPortal?.appRefName;

      setSelectedAppRefName(appRefName ?? "");

      // If appRefName is 'Other', then do not use the API
      // Instead, populate the Request Reasons from the full list found in i18next json
      // And leave the Country of Business list empty, because that list will be hidden for 'Other'
      // Also note:  We need to use "Other" when checking appRefName. Do not use the translated version of 'Other'
      if (appRefName === "Other" || appRefName === "Australia") {
        const reasons = defaultRequestReasons.map((item) => item.reason);
        setReasonForRequestData(reasons);
        // ⚾️ there is a race condition between setting
        // the dynamic state options of the request reasons drop down
        // and setting its value to empty
        // without this artificial delay, formik validation will
        // continue marking the form as valid, even when the country
        // and request reason values have been set to empty string
        // with the artificial delay, formik validate applies
        // validation and disables the submit request button as expected
        Promise.resolve(true).then(() => {
          return formik.validateForm();
        });
      } else if (appRefName && appRefName.length > 0) {
        fetch(countriesUrl + appRefName)
          .then((response) => response.json())
          .then((result) => {
            const countries = result?.data?.countries as
              | RequestedPortalCountry[]
              | undefined;

            if (countries) {
              const translatedCountries = getTranslatedCountries(countries);
              const countriesForDisplay = translatedCountries.map(
                (country) => country.translatedCountry
              );
              setCountryOfBusinessData(countriesForDisplay);
            }

            // Get the Request Access Reasons and sort the list
            const requestReasons = result?.data?.requestReasons;
            if (requestReasons) {
              const translatedReasons = getTranslatedReasons(requestReasons);
              const reasonsForDisplay = translatedReasons.map(
                (item) => item.reason
              );
              setReasonForRequestData(reasonsForDisplay);
            }
          })
          .then(() => {
            // ⚾️ similarly to the primary if-branch, above
            // we must manually force formik to revalidate
            // but this time we can rely on the delay of processing
            // the request so that the race condition does not impact
            // the form valid state
            formik.validateForm();
          });
      }
    }
  }

  /**
   * Set the selected Portal's corresponding appRefName
   */
  function setRequestedPortalAppRefName(e: any) {
    // Get the selected Portal name
    const requestedPortalName = e?.target?.value;

    const requestedPortalItem = requestedPortalData.find((item) => {
      return item.name === requestedPortalName;
    });
    if (requestedPortalItem) {
      formik.setFieldValue("appRefName", requestedPortalItem.appRefName);
    }
  }

  /**
   * Clear the Access Reason
   */
  function clearAccessReason() {
    formik.setFieldValue("accessReason", "");
  }

  /**
   * Get key words to check if it is a HR request
   */
  function getKeyWordsHRValidation() {
    const keywordsString = t(
      "request_access:more_information.key_words_hr_request"
    );
    let keywordsList = [];
    if (keywordsString.includes("、")) {
      keywordsList = keywordsString.split("、"); // to handle japanese and chinese
    } else {
      keywordsList = keywordsString.split(",");
    }
    return keywordsList;
  }

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Box>
          <Typography
            variant="h1"
            sx={{
              fontSize: "1.75em",
              fontWeight: "bold",
              paddingBottom: "24px",
            }}
          >
            {t("common:common.request_access")}
          </Typography>
        </Box>
        <Box sx={{ fontSize: "1em", marginBottom: "36px" }}>
          {t("request_access:employee_access.summary")}
        </Box>

        <Box
          sx={{
            marginBottom: "20px",
          }}
        >
          {/* Requested Portal */}
          <Box sx={{ textAlign: "left" }}>
            <InputLabel
              sx={{ textAlign: "left", mt: "1rem" }}
              htmlFor="cargillBusiness"
            >
              {t("request_access:more_information.requested_portal")}
              <IconButton
                sx={{
                  width: "16px",
                  height: "16px",
                  fontSize: "16px",
                  marginLeft: "8px",
                  marginBottom: "4px",
                }}
                onClick={() => {
                  setShowToolTip(true);
                }}
              >
                <InfoIcon
                  sx={{
                    fill: "#666666",
                  }}
                ></InfoIcon>
              </IconButton>
            </InputLabel>
            <FormControl fullWidth>
              <Select
                sx={{
                  paddingLeft: "0",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                }}
                id="cargillBusiness"
                name="cargillBusiness"
                value={formik.values.cargillBusiness}
                onChange={async (e) => {
                  setRequestedPortalAppRefName(e);
                  getRequestedPortalData(e);
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
              >
                {requestedPortalData.map(
                  (
                    item: { name: string; appRefName: string },
                    index: number
                  ) => (
                    <MenuItem key={index} value={item.name}>
                      {item.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            {formik.errors.cargillBusiness &&
              formik.touched.cargillBusiness && (
                <ErrorBox>{formik.errors.cargillBusiness}</ErrorBox>
              )}
          </Box>

          {/* Country of Business */}
          {formik.values.cargillBusiness !== other && (
            <Box sx={{ textAlign: "left" }}>
              <InputLabel
                sx={{ textAlign: "left", mt: "1rem" }}
                htmlFor="country"
              >
                {selectedAppRefName === "FIBI"
                  ? t("request_access:customer_access.business_region")
                  : t("request_access:customer_access.business_country")}
              </InputLabel>
              <FormControl fullWidth>
                <Select
                  sx={{
                    paddingLeft: "0",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                  }}
                  id="country"
                  name="country"
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  disabled={!formik.values.cargillBusiness}
                >
                  {countryOfBusinessData.map((item: string, index: number) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formik.errors.country && formik.touched.country && (
                <ErrorBox>{formik.errors.country}</ErrorBox>
              )}
            </Box>
          )}

          {/* Reason for Request */}
          <Box sx={{ textAlign: "left" }}>
            <InputLabel
              sx={{ textAlign: "left", mt: "1rem" }}
              htmlFor="requestReason"
            >
              {t("request_access:more_information.reason_for_request")}
            </InputLabel>
            <FormControl fullWidth>
              <Select
                sx={{
                  paddingLeft: "0",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                }}
                id="requestReason"
                name="requestReason"
                value={formik.values.requestReason}
                onChange={(e) => {
                  clearAccessReason();
                  formik.handleChange(e);
                }}
                disabled={!formik.values.cargillBusiness}
              >
                {reasonForRequestData.map((item: string, index: number) => (
                  <MenuItem key={index} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formik.errors.requestReason && formik.touched.requestReason && (
              <ErrorBox>{formik.errors.requestReason}</ErrorBox>
            )}
          </Box>

          {/* Explanation */}
          {formik.values.requestReason == other && (
            <Box sx={{ textAlign: "left" }}>
              <InputLabel
                sx={{ textAlign: "left", mt: "1rem" }}
                htmlFor="cargillBusiness"
              >
                {t("request_access:more_information.explanation")}
              </InputLabel>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  id="accessReason"
                  name="accessReason"
                  value={formik.values.accessReason}
                  placeholder={
                    t("request_access:more_information.tell_us_why") || ""
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormControl>
              {formik.errors.accessReason && formik.touched.accessReason && (
                <ErrorBox>{formik.errors.accessReason}</ErrorBox>
              )}
              {hrRequestKeywords.some((el) =>
                formik.values.accessReason.toLowerCase().includes(el)
              ) && (
                <Box
                  sx={{
                    backgroundColor: "hsla(0,0%,60%,.2)",
                    border: "1.5px solid #999",
                    p: 4,
                    mb: "28px",
                    mt: "28px",
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        t("request_access:more_information.go_to_myhr") || "",
                    }}
                  ></div>
                </Box>
              )}
            </Box>
          )}

          {/* Tooltip dialog box */}
          <Dialog
            open={showToolTip}
            onClose={() => {
              setShowToolTip(false);
            }}
          >
            <Box
              sx={{
                margin: "12px",
              }}
            >
              <Typography
                variant="h1"
                align="center"
                sx={{
                  fontSize: "1.75em",
                  fontWeight: "bold",
                }}
              >
                {t("request_access:more_information.requested_portal")}
              </Typography>
            </Box>
            <Box
              sx={{ marginLeft: "18px", marginRight: "18px" }}
              dangerouslySetInnerHTML={{
                __html: t("request_access:more_information.tool_tip") ?? "",
              }}
            ></Box>
            <Typography align="center">
              <Button
                sx={{ marginBottom: "16px" }}
                variant="contained"
                onClick={() => {
                  setShowToolTip(false);
                }}
              >
                {t("request_access:more_information.dismiss")}
              </Button>
            </Typography>
          </Dialog>
        </Box>
        <Button
          sx={{ width: "100%" }}
          type="submit"
          disabled={!formik.dirty || !formik.isValid}
        >
          {t("common:common.continue")}
        </Button>
      </form>
    </Box>
  );
}